import React, { useState, useEffect, useMemo } from 'react';
import { Power, RefreshCw, Eye, Send, AlertTriangle, Clock } from 'lucide-react';
import api from '../services/api';

// Apagado retardado: cuanto sigue prendido/grabando el MDVR despues de que se
// corta la ignicion (ACC off). Cada minuto de mas es bateria del camion y datos
// del hotspot, por eso la pagina existe: ver de un vistazo quien esta en 2 h.
//
// El valor NO vive en la BD: se le pregunta al equipo por ISAPI en vivo (por el
// tunel ISUP), asi que solo contestan los conectados. La lectura de la flota es
// secuencial a proposito, el engine tiene una sola sesion por equipo.

const OBJETIVO = 30;           // lo que se quiere dejar en la flota
const ESCALA_MAX = 360;        // tope de la lista que publican los equipos (6 h)

const fmtMin = (m) => {
  if (m === null || m === undefined || Number.isNaN(m)) return '—';
  if (m === 0) return 'inmediato';
  if (m < 60) return `${m} min`;
  const h = m / 60;
  return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1)} h`;
};

// Semaforo por cuanto se pasa del objetivo.
const nivel = (m) => {
  if (m === null || m === undefined) return { c: '#6b7280', bg: '#f3f4f6' };
  if (m >= 120) return { c: '#b91c1c', bg: '#fee2e2' };
  if (m > OBJETIVO) return { c: '#b45309', bg: '#fef3c7' };
  if (m === 0) return { c: '#6b7280', bg: '#f3f4f6' };
  return { c: '#047857', bg: '#d1fae5' };
};

const CONN = {
  wifi_sucursal: { c: '#059669', bg: '#d1fae5', label: 'WiFi sucursal' },
  samsara: { c: '#7c3aed', bg: '#ede9fe', label: 'Samsara' },
  datos_celular: { c: '#ca8a04', bg: '#fef3c7', label: 'Datos' },
  desconocido: { c: '#6b7280', bg: '#f3f4f6', label: '?' },
};
const connOf = (k) => CONN[k] || CONN.desconocido;

const Apagado = () => {
  const [rows, setRows] = useState([]);
  const [lect, setLect] = useState({});      // device_id -> lectura del equipo
  const [res, setRes] = useState({});        // device_id -> resultado del PUT
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);
  const [prog, setProg] = useState(null);    // {hechos, total} al leer la flota
  const [minutos, setMinutos] = useState(OBJETIVO);
  const [soloOnline, setSoloOnline] = useState(true);
  const [sucursal, setSucursal] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/mdvr/status');
      setRows(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sucursales = useMemo(
    () => [...new Set(rows.map((r) => r.sucursal).filter(Boolean))].sort(), [rows]);

  const shown = rows.filter((r) =>
    (!soloOnline || r.is_current) && (!sucursal || r.sucursal === sucursal));

  const minutosDe = (id) => {
    const l = lect[id];
    if (!l || !l.encontrado) return null;
    if (l.minutos !== null && l.minutos !== undefined) return Math.round(l.minutos);
    return l.campo ? l.campo.value : null;
  };

  const readOne = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await api.get(`/api/mdvr/${id}/power-delay`);
      setLect((c) => ({ ...c, [id]: r.data }));
      return r.data;
    } catch (e) {
      const err = { encontrado: false, error: e.response?.data?.detail || 'no responde' };
      setLect((c) => ({ ...c, [id]: err }));
      return err;
    } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  // Secuencial: al mismo equipo no se le puede hablar en paralelo por el tunel,
  // y en rafaga se atoran las sesiones ISUP.
  const readAll = async () => {
    const targets = shown.filter((r) => r.is_current);
    if (!targets.length) { alert('No hay equipos en linea en esta vista.'); return; }
    setProg({ hechos: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      await readOne(targets[i].device_id);
      setProg({ hechos: i + 1, total: targets.length });
    }
    setProg(null);
  };

  const applyOne = async (id) => {
    const actual = minutosDe(id);
    const aviso = `Cambiar el apagado retardado de ${id} a ${fmtMin(minutos)}?`
      + (actual !== null ? `\nAhora esta en ${fmtMin(actual)}.` : '')
      + '\n\nSolo tiene efecto si el cable de ACC esta conectado.';
    if (!window.confirm(aviso)) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await api.put(`/api/mdvr/${id}/power-delay`, { minutes: minutos });
      setRes((s) => ({ ...s, [id]: r.data }));
      if (r.data.ok) await readOne(id);
    } catch (e) {
      setRes((s) => ({ ...s, [id]: { ok: false, error: e.response?.data?.detail || 'error' } }));
    } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const applyBulk = async () => {
    const targets = shown.filter((r) => r.is_current);
    if (!targets.length) { alert('No hay equipos en linea en esta vista.'); return; }
    if (!window.confirm(
      `Aplicar ${fmtMin(minutos)} a ${targets.length} equipos en linea?\n\n` +
      'Va uno por uno; puede tardar un par de segundos por equipo.')) return;
    setLoading(true);
    try {
      const r = await api.post('/api/mdvr/power-delay/bulk',
        { minutes: minutos, device_ids: targets.map((m) => m.device_id) });
      const map = {};
      (r.data.resultados || []).forEach((x) => { map[x.device_id] = x; });
      setRes((s) => ({ ...s, ...map }));
      // Reflejar el valor nuevo sin volver a preguntarle a cada equipo.
      setLect((c) => {
        const n = { ...c };
        (r.data.resultados || []).forEach((x) => {
          if (x.ok) {
            n[x.device_id] = { ...(n[x.device_id] || {}), encontrado: true,
                               minutos, campo: { value: minutos } };
          }
        });
        return n;
      });
      alert(`Listo: ${r.data.ok}/${r.data.total} aplicados a ${fmtMin(minutos)}`);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  // Valores que declaran los equipos leidos: es una lista cerrada, no un rango.
  // Mientras nadie se haya leido se muestra la lista tipica del MDVR AE-MD.
  const opciones = useMemo(() => {
    const set = new Set();
    Object.values(lect).forEach((l) => {
      ((l && l.opciones) || []).forEach((o) => set.add(o));
    });
    const arr = [...set].sort((a, b) => a - b);
    return arr.length ? arr : [0, 5, 10, 15, 30, 45, 60, 120, 180, 240, 300, 360];
  }, [lect]);

  // Reparto de la flota leida: el grafico de arriba.
  const dist = useMemo(() => {
    const buckets = [
      { k: 'inmediato', test: (m) => m === 0, c: '#9ca3af', n: 0 },
      { k: '1-30 min', test: (m) => m > 0 && m <= 30, c: '#10b981', n: 0 },
      { k: '31-60 min', test: (m) => m > 30 && m <= 60, c: '#f59e0b', n: 0 },
      { k: '1-2 h', test: (m) => m > 60 && m <= 120, c: '#f97316', n: 0 },
      { k: 'mas de 2 h', test: (m) => m > 120, c: '#dc2626', n: 0 },
    ];
    let leidos = 0;
    let sinDato = 0;
    shown.forEach((r) => {
      const m = minutosDe(r.device_id);
      if (m === null) { if (lect[r.device_id]) sinDato++; return; }
      leidos++;
      const b = buckets.find((x) => x.test(m));
      if (b) b.n++;
    });
    return { buckets, leidos, sinDato };
  }, [shown, lect]);

  const sobreObjetivo = shown.filter((r) => {
    const m = minutosDe(r.device_id);
    return m !== null && m > OBJETIVO;
  }).length;

  const nOnline = shown.filter((r) => r.is_current).length;

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><Power size={26} /> Apagado retardado</h1>
          <p style={S.subtitle}>
            Cuanto sigue prendido el MDVR tras cortar la ignicion.
            {' '}{nOnline} en linea de {shown.length} en esta vista.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.refreshBtn} onClick={load}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} /> Equipos
          </button>
          <button style={S.readAllBtn} disabled={!!prog} onClick={readAll}>
            <Eye size={16} /> {prog ? `Leyendo ${prog.hechos}/${prog.total}...`
                                   : `Leer los ${nOnline} en linea`}
          </button>
        </div>
      </header>

      <div style={S.kpis}>
        <Kpi label="Leidos del equipo" value={dist.leidos} color="#2563eb" />
        <Kpi label={`Arriba de ${OBJETIVO} min`} value={sobreObjetivo} color="#dc2626" />
        <Kpi label="En linea" value={nOnline} color="#10b981" />
        <Kpi label="No lo expusieron" value={dist.sinDato} color="#6b7280" />
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}><Clock size={15} /> Reparto de la flota leida</div>
        {dist.leidos === 0 ? (
          <div style={S.vacio}>Nadie leido todavia. Dale a <b>Leer los {nOnline} en linea</b>.</div>
        ) : (
          <>
            <div style={S.barra}>
              {dist.buckets.filter((b) => b.n).map((b) => (
                <div key={b.k} title={`${b.k}: ${b.n}`}
                     style={{ ...S.seg, width: `${(b.n / dist.leidos) * 100}%`, background: b.c }}>
                  {b.n}
                </div>
              ))}
            </div>
            <div style={S.leyenda}>
              {dist.buckets.map((b) => (
                <span key={b.k} style={S.legItem}>
                  <i style={{ ...S.legDot, background: b.c, opacity: b.n ? 1 : 0.3 }} />
                  {b.k} <b>{b.n}</b>
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      <div style={S.warnBox}>
        <AlertTriangle size={18} color="#b45309" />
        <span>El retardo <b>solo aplica si el cable de ACC/ignicion esta conectado</b>: si el equipo
          cuelga directo de bateria nunca ve el apagado. El valor se escribe en el equipo y se
          <b> verifica releyendo</b>, y los equipos aceptan una <b>lista cerrada</b> de valores
          (no cualquier numero).</span>
      </div>

      <div style={S.bulkRow}>
        <div style={S.field}>
          <label style={S.lab}>Dejar en</label>
          <select style={S.select} value={minutos}
                  onChange={(e) => setMinutos(Number(e.target.value))}>
            {opciones.map((o) => <option key={o} value={o}>{fmtMin(o)}</option>)}
          </select>
        </div>
        <select style={S.select} value={sucursal} onChange={(e) => setSucursal(e.target.value)}>
          <option value="">Todas las sucursales</option>
          {sucursales.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={S.check}>
          <input type="checkbox" checked={soloOnline}
                 onChange={(e) => setSoloOnline(e.target.checked)} /> Solo en linea
        </label>
        <button style={{ ...S.bulkBtn, opacity: nOnline ? 1 : 0.5 }}
                disabled={!nOnline || loading} onClick={applyBulk}>
          <Send size={15} /> Aplicar a los {nOnline} en linea
        </button>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Equipo</th><th style={S.th}>Conexion</th><th style={S.th}>Estado</th>
            <th style={S.th}>Apagado actual</th><th style={S.th}>Escala (0 a 6 h)</th>
            <th style={S.th}>Resultado</th><th style={S.th}>Acciones</th>
          </tr></thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={7} style={S.tdEmpty}>Sin equipos en este filtro</td></tr>
            )}
            {shown.map((m) => {
              const id = m.device_id;
              const con = connOf(m.connection_type);
              const min = minutosDe(id);
              const nv = nivel(min);
              const l = lect[id];
              const r = res[id];
              return (
                <tr key={id} style={{ ...S.tr, opacity: m.is_current ? 1 : 0.55 }}>
                  <td style={S.td}>
                    <b>{m.numero_unidad ? `MDVR ${m.numero_unidad}` : id}</b>
                    <div style={S.sub}>{id}{m.sucursal ? ` · ${m.sucursal}` : ''}</div>
                  </td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: con.bg, color: con.c }}>{con.label}</span>
                  </td>
                  <td style={S.td}>
                    <span style={{ fontSize: 12, color: m.is_current ? '#059669' : '#9ca3af' }}>
                      {m.is_current ? '● online' : '○ offline'}
                    </span>
                  </td>
                  <td style={S.td}>
                    {min !== null
                      ? <span style={{ ...S.badge, background: nv.bg, color: nv.c, fontSize: 13 }}>
                          {fmtMin(min)}
                        </span>
                      : <span style={S.sub}>{l ? (l.error || 'no lo expone') : '—'}</span>}
                  </td>
                  <td style={{ ...S.td, minWidth: 180 }}>
                    <div style={S.track}>
                      <div style={{ ...S.fill, background: nv.c,
                                    width: `${Math.min((min || 0) / ESCALA_MAX, 1) * 100}%` }} />
                      <div style={{ ...S.meta, left: `${(OBJETIVO / ESCALA_MAX) * 100}%` }}
                           title={`objetivo ${OBJETIVO} min`} />
                    </div>
                  </td>
                  <td style={S.td}>
                    {r && (r.ok
                      ? <span style={S.ok}>✓ {fmtMin(Number(r.valor_nuevo))}</span>
                      : <span style={S.bad} title={r.error}>✕ {(r.error || 'error').slice(0, 40)}</span>)}
                  </td>
                  <td style={S.td}>
                    <button style={S.btnRead} disabled={busy[id] || !m.is_current}
                            onClick={() => readOne(id)}><Eye size={13} /> Leer</button>
                    <button style={S.btnApply} disabled={busy[id] || !m.is_current}
                            onClick={() => applyOne(id)}><Send size={13} /> {fmtMin(minutos)}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const Kpi = ({ label, value, color }) => (
  <div style={S.kpi}>
    <div style={{ ...S.kpiVal, color }}>{value}</div>
    <div style={S.kpiLab}>{label}</div>
  </div>
);

const S = {
  container: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' },
  title: { fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  refreshBtn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },
  readAllBtn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px', background: '#111827', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 14 },
  kpi: { background: 'white', padding: 14, borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  kpiVal: { fontSize: 26, fontWeight: 700, lineHeight: 1 },
  kpiLab: { fontSize: 12, color: '#6b7280', marginTop: 6 },
  card: { background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)', marginBottom: 14 },
  cardTitle: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 },
  vacio: { fontSize: 13, color: '#9ca3af', padding: '10px 0' },
  barra: { display: 'flex', height: 26, borderRadius: 6, overflow: 'hidden', background: '#f3f4f6' },
  seg: { display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12, fontWeight: 700, minWidth: 18 },
  leyenda: { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10, fontSize: 12, color: '#6b7280' },
  legItem: { display: 'flex', alignItems: 'center', gap: 5 },
  legDot: { width: 10, height: 10, borderRadius: 3, display: 'inline-block' },
  warnBox: { display: 'flex', gap: 10, alignItems: 'flex-start', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 14 },
  bulkRow: { display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap' },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  lab: { fontSize: 12, color: '#6b7280' },
  select: { padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, minWidth: 160, background: 'white' },
  check: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#374151', paddingBottom: 8 },
  bulkBtn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  tableWrap: { background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 14px', background: '#f9fafb', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 14px', color: '#374151', verticalAlign: 'middle' },
  sub: { fontSize: 11, color: '#9ca3af' },
  tdEmpty: { padding: 40, textAlign: 'center', color: '#9ca3af' },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, display: 'inline-block' },
  track: { position: 'relative', height: 8, background: '#f3f4f6', borderRadius: 4 },
  fill: { height: 8, borderRadius: 4 },
  meta: { position: 'absolute', top: -3, width: 2, height: 14, background: '#111827', opacity: 0.35 },
  btnRead: { padding: '5px 10px', marginRight: 6, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 },
  btnApply: { padding: '5px 10px', background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 },
  ok: { color: '#059669', fontWeight: 600 },
  bad: { color: '#dc2626' },
};

export default Apagado;
